import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteLoaderData,
} from "@remix-run/react";
import { json } from "@vercel/remix";
import type { LinksFunction, MetaFunction } from "@vercel/remix";
import tailwind from "@figment-fitness/ui/styles/tailwind.css?url";

import "@figment-fitness/ui"; // for importing style.css
import "@figment-fitness/ui/dist/index.css"; // for importing style.css
import "@radix-ui/themes/styles.css";
import { Theme } from "@figment-fitness/ui/components/theme";
import { initConfig } from "~/client-config";
import { Toaster } from "@figment-fitness/ui/components/toast";
import DefaultErrorBoundary from "@figment-fitness/ui/components/error-boundary";

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: tailwind },
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Zen+Kaku+Gothic+New:wght@400;500;700&display=swap",
  },
];

// 医療×フィットネスの文脈に合わせたテーマ。落ち着いた teal +
// 温かみのある sage グレー + 大きめ角丸 + 日本語ゴシック。
const themeFontFamily =
  '"Zen Kaku Gothic New", "Hiragino Kaku Gothic ProN", "Noto Sans JP", sans-serif';

export const loader = async () => {
  return json({
    ENV: {
      PUBLIC_APP_ENV: process.env.PUBLIC_APP_ENV,
    },
  });
};

export const meta: MetaFunction = () => {
  return [
    { title: "Figment管理画面" },
    { name: "description", content: "Figment system" },
  ];
};

export const config = {
  maxDuration: 30,
  memory: 1024,
};

export function Layout({ children }: { children: React.ReactNode }) {
  // Layout はエラー発生時（ErrorBoundary 表示時）も描画されるが、その際は
  // root loader のデータが存在しない。useLoaderData はその場合に例外を
  // 投げてしまう（"You cannot useLoaderData in an errorElement"）ため、
  // undefined を安全に返す useRouteLoaderData を使う。
  const data = useRouteLoaderData<typeof loader>("root");
  const appENV = data?.ENV?.PUBLIC_APP_ENV;
  if (appENV) initConfig(appENV);

  return (
    <html lang="ja">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        <Theme
          accentColor="teal"
          grayColor="sage"
          radius="large"
          scaling="100%"
          style={{ ["--default-font-family" as string]: themeFontFamily }}
        >
          {children}
          <Toaster />
        </Theme>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export const ErrorBoundary = DefaultErrorBoundary;

export default function App() {
  return <Outlet />;
}
